// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/RelatorioPos.tsx
//
//  Fase 3 · Iter 3.3 — Relatório analítico de Pedidos de Compra.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, DollarSign, FileWarning, Link2, ShoppingCart, XCircle,
} from 'lucide-react';
import { format, subMonths } from 'date-fns';
import {
  Cell, Pie, PieChart, ResponsiveContainer, Tooltip,
} from 'recharts';

import { useRelatorioPos } from '@/features/compras/hooks/useRecebimento';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmtMoeda = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (s?: string) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); }
  catch { return s; }
};

const CORES_STATUS: Record<string, string> = {
  Rascunho:              '#94a3b8',
  Pendente:              '#94a3b8',
  EmAprovacao:           '#a855f7',
  Aprovada:              '#3b82f6',
  EnviadaFornecedor:     '#06b6d4',
  ConfirmadaFornecedor:  '#0ea5e9',
  ParcialmenteRecebida:  '#f59e0b',
  TotalmenteRecebida:    '#22c55e',
  Cancelada:             '#ef4444',
};

const STATUS_OPCOES = [
  'Todos', 'Rascunho', 'Pendente', 'EmAprovacao', 'Aprovada',
  'EnviadaFornecedor', 'ConfirmadaFornecedor', 'ParcialmenteRecebida',
  'TotalmenteRecebida', 'Cancelada',
];

const RelatorioPos = () => {
  const hoje = new Date();
  const [dataInicio, setDataInicio] = useState(format(subMonths(hoje, 3), 'yyyy-MM-dd'));
  const [dataFim,    setDataFim]    = useState(format(hoje, 'yyyy-MM-dd'));
  // USA-COM-15: o backend já filtra por fornecedor.
  const [fornecedorId, setFornecedorId] = useState<number | null>(null);
  const [status,     setStatus]     = useState<string>('Todos');

  const { data, isLoading } = useRelatorioPos({
    dataInicio,
    dataFim,
    status: status === 'Todos' ? undefined : status,
    fornecedorId: fornecedorId ?? undefined,
  });

  const kpis = data?.resumo;
  const pedidos = useMemo(() => data?.pedidos ?? [], [data]);
  const pagPedidos = usePaginacaoLocal(pedidos); // USA-COM-17
  const dist   = data?.distribuicaoPorStatus ?? [];
  const topDiv  = data?.topDivergenciaPreco ?? [];

  const distPie = dist.map((d) => ({
    nome:   d.status,
    valor:  d.quantidade,
    fill:   CORES_STATUS[d.status] ?? '#64748b',
  }));

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Relatório de Pedidos de Compra</h1>
        <p className="text-muted-foreground mt-1">
          POs emitidas no período: status, atraso de entrega, 3-way match e divergência de preço
          entre pedido e recebimento.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Data inicial</Label>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Data final</Label>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="space-y-1 w-72">
            <Label>Fornecedor</Label>
            <FornecedorCombobox value={fornecedorId} onChange={setFornecedorId}
              placeholder="Todos os fornecedores" />
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPCOES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {isLoading || !kpis ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <KpiCard
            icon={<ShoppingCart className="h-4 w-4 text-blue-500" />}
            label="Total de POs"
            valor={String(kpis.totalPOs)}
            subtitulo={fmtMoeda(kpis.valorTotalPedidos)}
          />
          <KpiCard
            icon={<AlertTriangle className="h-4 w-4 text-destructive" />}
            label="Atrasadas"
            valor={String(kpis.pOsAtrasadas)}
            destaque={kpis.pOsAtrasadas > 0 ? 'destructive' : undefined}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
            label="Totalmente recebidas"
            valor={String(kpis.pOsCompletamenteRecebidas)}
            subtitulo={`+${kpis.pOsParcialmenteRecebidas} parcialmente`}
          />
          <KpiCard
            icon={<Link2 className="h-4 w-4 text-amber-500" />}
            label="3-way match"
            valor={String(kpis.pOsComThreeWayMatch)}
          />
          <KpiCard
            icon={<DollarSign className="h-4 w-4 text-purple-500" />}
            label="Divergência média"
            valor={`${kpis.divergenciaMediaPrecoPercent.toFixed(2)}%`}
          />
        </div>
      )}

      {/* Distribuição por status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por status</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : distPie.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Sem POs.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distPie}
                    dataKey="valor"
                    nameKey="nome"
                    cx="50%" cy="50%"
                    outerRadius={90}
                    label={(e) => `${e.nome}: ${e.valor}`}
                    labelLine={false}
                  >
                    {distPie.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileWarning className="h-4 w-4 text-amber-500" />
              Top 10 — divergência de preço
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : topDiv.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem divergências.</p>
            ) : (
              <div className="rounded-md border overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO</TableHead>
                      <TableHead>Fornecedor</TableHead>
                      <TableHead className="text-right">Valor PO</TableHead>
                      <TableHead className="text-right">Divergência</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topDiv.map((p) => (
                      <TableRow key={p.pedidoId}>
                        <TableCell className="font-mono text-xs">#{p.numero}</TableCell>
                        <TableCell className="max-w-[180px] truncate" title={p.fornecedorNome ?? ''}>
                          {p.fornecedorNome ?? '(sem nome)'}
                        </TableCell>
                        <TableCell className="text-right text-xs">{fmtMoeda(p.valorPedido)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          <span className={p.divergenciaPrecoPercent >= 0 ? 'text-destructive' : 'text-green-600'}>
                            {p.divergenciaPrecoPercent.toFixed(2)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabela completa */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pedidos de Compra no período</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : pedidos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma PO encontrada.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO</TableHead>
                    <TableHead>Emissão</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Recebido</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Entrega</TableHead>
                    <TableHead>Atraso</TableHead>
                    <TableHead className="text-right">Divergência</TableHead>
                    <TableHead>3WM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagPedidos.itensPaginados.map((p) => (
                    <TableRow key={p.pedidoId} className={p.isAtrasado ? 'bg-destructive/5' : ''}>
                      <TableCell className="font-mono text-xs">#{p.numero}</TableCell>
                      <TableCell className="text-xs">{fmtData(p.dtEmissao)}</TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm" title={p.fornecedorNome ?? ''}>
                        {p.fornecedorNome ?? '(sem nome)'}
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmtMoeda(p.valorPedido)}</TableCell>
                      <TableCell className="text-right text-xs">
                        {p.valorRecebido > 0 ? fmtMoeda(p.valorRecebido) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          style={{
                            backgroundColor: `${CORES_STATUS[p.status] ?? '#64748b'}20`,
                            color: CORES_STATUS[p.status] ?? '#64748b',
                            borderColor: `${CORES_STATUS[p.status] ?? '#64748b'}60`,
                          }}
                          className="text-xs"
                        >
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>Prev.: {fmtData(p.dtEntregaPrevista)}</div>
                        {p.dtPrimeiroRecebimento && (
                          <div className="text-muted-foreground">1º: {fmtData(p.dtPrimeiroRecebimento)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.diasAtraso == null ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : p.isAtrasado ? (
                          <Badge variant="destructive" className="text-xs">+{p.diasAtraso}d</Badge>
                        ) : p.diasAtraso < 0 ? (
                          <Badge className="text-xs bg-green-500/10 text-green-700 border-0" variant="secondary">
                            {p.diasAtraso}d
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">no prazo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {p.divergenciaPrecoPercent === 0 ? '—' : (
                          <span className={p.divergenciaPrecoPercent >= 0 ? 'text-destructive' : 'text-green-600'}>
                            {p.divergenciaPrecoPercent.toFixed(2)}%
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.temThreeWayMatch ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {pedidos.length > 0 && (
            <div className="mt-4">
              <PaginacaoRodape
                pagina={pagPedidos.pagina}
                totalPaginas={pagPedidos.totalPaginas}
                totalItens={pagPedidos.totalItens}
                primeiroItem={pagPedidos.primeiroItem}
                ultimoItem={pagPedidos.ultimoItem}
                tamanhoPagina={pagPedidos.tamanhoPagina}
                onPaginaChange={pagPedidos.setPagina}
                onTamanhoPaginaChange={pagPedidos.setTamanhoPagina}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RelatorioPos;

// ── KpiCard ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon, label, valor, subtitulo, destaque,
}: {
  icon:       React.ReactNode;
  label:      string;
  valor:      string;
  subtitulo?: string;
  destaque?:  'destructive' | 'warning';
}) {
  const borda =
    destaque === 'destructive' ? 'border-destructive/50'
    : destaque === 'warning'    ? 'border-yellow-500/50'
    : '';
  return (
    <Card className={borda}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-normal">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{valor}</div>
        {subtitulo && <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>}
      </CardContent>
    </Card>
  );
}
